import React, { useCallback, useEffect, useMemo, useState } from "react";
import { get, post, put } from "aws-amplify/api";
import { Alert, Button, Card, Col, Form, Nav, Row } from "react-bootstrap";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createSLMEntityDetailElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/Detail.css";
import "./SLMEntityDetail.css";

const defaultEntity = {
  entity_org_id: null,
  sale_id: null,
  lease_id: null,
  name: "",
  name_key: "",
  mail_street: "",
  mail_state: null,
  mail_suburb: "",
  mail_code: "",
  phys_street: "",
  phys_state: null,
  phys_suburb: "",
  phys_code: "",
  phone_ext: "",
  phone_no: "",
  fax_ext: "",
  fax_no: "",
  email: "",
  web: "",
  notes: "",
  created_date: null,
  created_by: null,
  last_edited_date: null,
  last_edited_by: null,
};

const normalizeStateOption = (option) => {
  if (!option) {
    return null;
  }

  const value = option.value ?? option.state_code ?? option.code ?? null;
  const label = option.label ?? option.state_name ?? option.name ?? value;

  if (value === null || label === null) {
    return null;
  }

  return {
    value: String(value),
    label: String(label),
  };
};

const toStateOptionByValue = (stateValue, stateOptions) => {
  if (!stateValue) {
    return null;
  }

  return stateOptions.find((opt) => String(opt.value) === String(stateValue)) || {
    value: String(stateValue),
    label: String(stateValue),
  };
};

const parseResponseJson = async (operation) => {
  const response = await operation.response;
  return response.body.json();
};

function SLMEntityDetail({ isAuthenticated, user }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const createId = useMemo(() => createElementIdFactory("slm-entity-detail"), []);
  const elementIds = useMemo(() => createSLMEntityDetailElementIds(createId), [createId]);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const [isLoading, setIsLoading] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isEntityDirty, setIsEntityDirty] = useState(false);
  const [entity, setEntity] = useState(defaultEntity);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedPhysState, setSelectedPhysState] = useState(null);
  const [selectedMailState, setSelectedMailState] = useState(null);
  const [entityContacts, setEntityContacts] = useState([]);
  const [idList, setIdList] = useState([]);
  const [currentIdIndex, setCurrentIdIndex] = useState(null);

  const isNewEntity = routeId === "new";
  const previousId = currentIdIndex !== null && currentIdIndex > 0 ? idList[currentIdIndex - 1] : null;
  const nextId = currentIdIndex !== null && currentIdIndex + 1 < idList.length ? idList[currentIdIndex + 1] : null;
  const currentEntityId = entity.entity_org_id ?? (isNewEntity ? null : Number(routeId));

  const setEntityField = useCallback((field, value) => {
    setEntity((previous) => ({
      ...previous,
      [field]: value,
    }));
    setIsEntityDirty(true);
  }, []);

  const sanitizePostcode = useCallback((rawValue) => String(rawValue || "").replace(/\D/g, "").slice(0, 4), []);

  const hydrateBrowseState = useCallback((incomingIdList, currentId) => {
    const safeList = Array.isArray(incomingIdList)
      ? incomingIdList.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [];

    if (safeList.length === 0 || !Number.isFinite(currentId)) {
      setIdList([]);
      setCurrentIdIndex(null);
      return;
    }

    const resolvedIndex = safeList.indexOf(currentId);
    setIdList(safeList);
    setCurrentIdIndex(resolvedIndex >= 0 ? resolvedIndex : null);
  }, []);

  const getEntityContacts = useCallback(async (entityOrgId) => {
    if (!entityOrgId) {
      setEntityContacts([]);
      return;
    }

    try {
      const nestedPathContacts = await parseResponseJson(
        get({
          apiName: "colin",
          path: `/slmentities/${entityOrgId}/slmcontacts`,
          options: { queryParams: { orgid: entityOrgId } },
        })
      );

      setEntityContacts(Array.isArray(nestedPathContacts) ? nestedPathContacts : []);
    } catch (firstError) {
      try {
        const fallbackContacts = await parseResponseJson(
          get({
            apiName: "colin",
            path: "/slmcontacts",
            options: { queryParams: { orgid: entityOrgId } },
          })
        );

        setEntityContacts(Array.isArray(fallbackContacts) ? fallbackContacts : []);
      } catch (fallbackError) {
        console.error("Failed to load entity contacts", firstError, fallbackError);
        setEntityContacts([]);
      }
    }
  }, []);

  const loadStates = useCallback(async () => {
    try {
      const statesResponse = await parseResponseJson(
        get({ apiName: "colin", path: "/slmstates", options: { queryParams: {} } })
      );

      const normalizedStates = (Array.isArray(statesResponse) ? statesResponse : [])
        .map((option) => normalizeStateOption(option))
        .filter(Boolean);

      setStateOptions(normalizedStates);
      return normalizedStates;
    } catch (error) {
      console.error("Failed to load SLM states", error);
      setStateOptions([]);
      return [];
    }
  }, []);

  const loadEntity = useCallback(async (entityId, availableStates) => {
    const loadedEntity = await parseResponseJson(
      get({
        apiName: "colin",
        path: `/slmentities/${entityId}`,
        options: { queryParams: {} },
      })
    );

    const normalizedEntity = {
      ...defaultEntity,
      ...loadedEntity,
    };

    setEntity(normalizedEntity);
    setSelectedPhysState(toStateOptionByValue(normalizedEntity.phys_state, availableStates));
    setSelectedMailState(toStateOptionByValue(normalizedEntity.mail_state, availableStates));

    await getEntityContacts(normalizedEntity.entity_org_id);

    document.title = normalizedEntity.name || "SLM Entity";
  }, [getEntityContacts]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isMounted = true;

    (async () => {
      setIsLoading(true);
      setShowAlert(false);
      setErrorMessage("");

      try {
        const availableStates = await loadStates();

        if (!isMounted) {
          return;
        }

        if (isNewEntity) {
          setEntity(defaultEntity);
          setSelectedPhysState(null);
          setSelectedMailState(null);
          setEntityContacts([]);
          document.title = "New entity record";
        } else {
          await loadEntity(routeId, availableStates);

          const routeIds = location?.state?.data;
          hydrateBrowseState(routeIds, Number(routeId));
        }
      } catch (error) {
        console.error("Failed to load entity", error);
        if (isMounted) {
          setShowAlert(true);
          setErrorMessage("Error loading entity details. Please refresh and try again.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [hydrateBrowseState, isAuthenticated, isNewEntity, loadEntity, loadStates, location?.state?.data, routeId]);

  const validateRecord = useCallback(() => {
    if (!entity.name || !String(entity.name).trim()) {
      return "You must enter a name.";
    }

    if (!entity.phys_state || !String(entity.phys_state).trim()) {
      return "Street Address State is a required field.";
    }

    return "";
  }, [entity.name, entity.phys_state]);

  const buildEntityPayload = useCallback(() => ({
    ...entity,
    last_edited_by: user,
    created_by: entity.created_by || user,
  }), [entity, user]);

  const saveEntity = useCallback(async () => {
    const validationError = validateRecord();

    if (validationError) {
      setShowAlert(true);
      setErrorMessage(validationError);
      return false;
    }

    setShowAlert(false);
    setErrorMessage("");
    try {
      const payload = buildEntityPayload();

      if (isNewEntity || !entity.entity_org_id) {
        const created = await parseResponseJson(
          post({
            apiName: "colin",
            path: "/slmentities/new",
            options: { body: payload },
          })
        );

        if (created?.result === false) {
          throw new Error(created?.error_message || "Unable to save entity");
        }

        const newId = created?.id;
        if (newId) {
          setIsEntityDirty(false);
          navigate(`/slmentities/${newId}`);
        }
      } else {
        const updated = await parseResponseJson(
          put({
            apiName: "colin",
            path: `/slmentities/${entity.entity_org_id}`,
            options: { body: payload },
          })
        );

        if (updated?.result === false) {
          throw new Error(updated?.error_message || "Unable to update entity");
        }

        setIsEntityDirty(false);
      }

      return true;
    } catch (error) {
      console.error("Failed to save entity", error);
      setShowAlert(true);
      setErrorMessage(error?.message || "Failed to save entity.");
      return false;
    }
  }, [buildEntityPayload, entity.entity_org_id, isNewEntity, navigate, validateRecord]);

  const handleEntityBlur = useCallback(async () => {
    if (!isEntityDirty) {
      return;
    }

    await saveEntity();
  }, [isEntityDirty, saveEntity]);

  const handleBrowseToRecord = useCallback((recordId) => {
    if (!recordId) {
      return;
    }

    navigate(`/slmentities/${recordId}`, { state: { data: idList } });
  }, [idList, navigate]);

  const handleCurrentIndexChange = useCallback((event) => {
    const nextValue = event.target.value.replace(/[^0-9]/g, "");

    if (!nextValue) {
      setCurrentIdIndex(null);
      return;
    }

    const requestedIndex = Number(nextValue) - 1;
    if (Number.isFinite(requestedIndex) && requestedIndex >= 0 && requestedIndex < idList.length) {
      setCurrentIdIndex(requestedIndex);
    }
  }, [idList.length]);

  const updateContactState = useCallback((rowIndex, columnId, value) => {
    setEntityContacts((previous) =>
      previous.map((record, index) => {
        if (index !== rowIndex) {
          return record;
        }

        return {
          ...record,
          [columnId]: value,
          modified: true,
        };
      })
    );
  }, []);

  const saveContact = useCallback(
    async ({ row, column, value }) => {
      const rowIndex = row?.index;
      if (!Number.isInteger(rowIndex) || rowIndex < 0) {
        return false;
      }

      const sourceRecord = entityContacts[rowIndex];
      if (!sourceRecord) {
        return false;
      }

      if (!currentEntityId) {
        setShowAlert(true);
        setErrorMessage("Save the entity before adding contacts.");
        return false;
      }

      const nextRecord = {
        ...sourceRecord,
        [column.id]: value,
        entity_org_id: currentEntityId,
        last_edited_by: user,
      };

      const isNew = Boolean(nextRecord.new_record || !nextRecord.entity_person_id);
      const path = isNew
        ? `/slmentities/${currentEntityId}/slmcontacts/new`
        : `/slmentities/${currentEntityId}/slmcontacts/${nextRecord.entity_person_id}`;

      try {
        const operation = isNew
          ? post({ apiName: "colin", path, options: { body: nextRecord } })
          : put({ apiName: "colin", path, options: { body: nextRecord } });

        const result = await parseResponseJson(operation);

        if (result?.result === false) {
          setShowAlert(true);
          setErrorMessage(result?.error_message || "Unable to save contact.");
          return false;
        }

        setEntityContacts((previous) =>
          previous.map((record, index) => {
            if (index !== rowIndex) {
              return record;
            }

            return {
              ...record,
              ...nextRecord,
              entity_person_id: result?.id ?? record.entity_person_id,
              new_record: false,
              modified: false,
            };
          })
        );

        setShowAlert(false);
        setErrorMessage("");
        return true;
      } catch (error) {
        console.error("Failed to save entity contact", error);
        setShowAlert(true);
        setErrorMessage("Failed to save entity contact.");
        return false;
      }
    },
    [currentEntityId, entityContacts, user]
  );

  const addContactRecord = useCallback(() => {
    if (!currentEntityId) {
      setShowAlert(true);
      setErrorMessage("Save the entity before adding contacts.");
      return;
    }

    setEntityContacts((previous) => [
      {
        entity_org_id: currentEntityId,
        entity_person_id: null,
        title: "",
        first_name: "",
        last_name: "",
        position: "",
        phone_ext: "",
        phone_no: "",
        email: "",
        notes: "",
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: false,
      },
      ...previous,
    ]);
  }, [currentEntityId, user]);

  const contactsColumns = useMemo(() => {
    const asDisplay = (value) => (value === null || value === undefined ? "" : value);

    return [
      columnHelper.accessor("entity_person_id", {
        id: "entity_person_id",
        header: "Contact ID",
        cell: (info) => asDisplay(info.getValue()),
        size: 110,
      }),
      columnHelper.accessor("title", {
        id: "title",
        header: "Salutation",
        cell: EditableCell,
        size: 130,
      }),
      columnHelper.accessor("first_name", {
        id: "first_name",
        header: "First Name",
        cell: EditableCell,
        size: 150,
      }),
      columnHelper.accessor("last_name", {
        id: "last_name",
        header: "Last Name",
        cell: EditableCell,
        size: 150,
      }),
      columnHelper.accessor("position", {
        id: "position",
        header: "Position",
        cell: EditableCell,
        size: 140,
      }),
      columnHelper.accessor("phone_ext", {
        id: "phone_ext",
        header: "Phone Ext.",
        cell: EditableCell,
        size: 120,
      }),
      columnHelper.accessor("phone_no", {
        id: "phone_no",
        header: "Phone No.",
        cell: EditableCell,
        size: 130,
      }),
      columnHelper.accessor("email", {
        id: "email",
        header: "Email",
        cell: EditableCell,
        size: 190,
      }),
      columnHelper.accessor("notes", {
        id: "notes",
        header: "Notes",
        cell: EditableCell,
        size: 200,
      }),
    ];
  }, [columnHelper]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="Entity slm-entity-detail" id={elementIds.root}>
      {isLoading && (
        <div className="outer-div">
          <div className="sweet-loading inner-div">
            <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
          </div>
        </div>
      )}

      {!isLoading && (
        <Form id={elementIds.form} className="slm-entity-detail-form">
          <Alert show={showAlert} variant="danger" onClose={() => setShowAlert(false)} dismissible>
            {errorMessage}
          </Alert>

          <Row className="align-items-center g-2 mb-2">
            <Col xs={12} lg={4}>
              <h6 id={elementIds.title} className="mb-0">
                {entity.name ? `Entity Details - ${entity.name}` : "Entity Details"}
              </h6>
            </Col>
            <Col xs={12} lg={4}>
              <Nav className="justify-content-lg-center flex-wrap gap-2">
                {previousId !== null && (
                  <Button id={elementIds.previousButton} type="button" variant="info" size="sm" onClick={() => handleBrowseToRecord(previousId)}>
                    Previous
                  </Button>
                )}
                {nextId !== null && (
                  <Button id={elementIds.nextButton} type="button" variant="info" size="sm" onClick={() => handleBrowseToRecord(nextId)}>
                    Next
                  </Button>
                )}
                {idList.length > 0 && (
                  <Form.Control
                    id={elementIds.browseIndexInput}
                    value={currentIdIndex === null ? "" : currentIdIndex + 1}
                    onChange={handleCurrentIndexChange}
                    onBlur={() => handleBrowseToRecord(idList[currentIdIndex])}
                    type="text"
                    className="slm-entity-detail-page-input"
                    aria-label="Current record index"
                  />
                )}
              </Nav>
            </Col>
            <Col xs={12} lg={4} className="d-flex justify-content-lg-end" />
          </Row>

          <Row className="g-3 slm-entity-detail-section">
            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Entity</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="entity_org_id">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Entity ID</Form.Label>
                    <Col sm={8}>
                      <Form.Control value={entity.entity_org_id || ""} type="text" disabled readOnly />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId={elementIds.nameInput}>
                    <Form.Label column sm={4} className="text-sm-end mb-0">Name</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.nameInput}
                        value={entity.name || ""}
                        onChange={(event) => setEntityField("name", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phone_ext">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Telephone</Form.Label>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.phoneExtInput}
                        value={entity.phone_ext || ""}
                        onChange={(event) => setEntityField("phone_ext", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.phoneNoInput}
                        value={entity.phone_no || ""}
                        onChange={(event) => setEntityField("phone_no", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="fax_ext">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Fax</Form.Label>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.faxExtInput}
                        value={entity.fax_ext || ""}
                        onChange={(event) => setEntityField("fax_ext", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                    <Col sm={4}>
                      <Form.Control
                        id={elementIds.faxNoInput}
                        value={entity.fax_no || ""}
                        onChange={(event) => setEntityField("fax_no", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="web">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Website</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.webInput}
                        value={entity.web || ""}
                        onChange={(event) => setEntityField("web", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="email">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Email</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.emailInput}
                        value={entity.email || ""}
                        onChange={(event) => setEntityField("email", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Street Address</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_street">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Street Address</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physStreetInput}
                        value={entity.phys_street || ""}
                        onChange={(event) => setEntityField("phys_street", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_suburb">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Suburb</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physSuburbInput}
                        value={entity.phys_suburb || ""}
                        onChange={(event) => setEntityField("phys_suburb", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="phys_state">
                    <Form.Label column sm={4} className="text-sm-end mb-0">State</Form.Label>
                    <Col sm={8}>
                      <Select
                        inputId={elementIds.physStateSelect}
                        value={selectedPhysState}
                        onChange={(selection) => {
                          setSelectedPhysState(selection);
                          setEntityField("phys_state", selection?.value || null);
                        }}
                        onBlur={handleEntityBlur}
                        options={stateOptions}
                        isClearable
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="phys_code">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postcode</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.physCodeInput}
                        value={entity.phys_code || ""}
                        onChange={(event) => setEntityField("phys_code", sanitizePostcode(event.target.value))}
                        onBlur={handleEntityBlur}
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card h-100">
                <Card.Header>Postal Address</Card.Header>
                <Card.Body>
                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_street">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postal Address</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailStreetInput}
                        value={entity.mail_street || ""}
                        onChange={(event) => setEntityField("mail_street", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_suburb">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Suburb</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailSuburbInput}
                        value={entity.mail_suburb || ""}
                        onChange={(event) => setEntityField("mail_suburb", event.target.value)}
                        onBlur={handleEntityBlur}
                        type="text"
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-2 align-items-center" controlId="mail_state">
                    <Form.Label column sm={4} className="text-sm-end mb-0">State</Form.Label>
                    <Col sm={8}>
                      <Select
                        inputId={elementIds.mailStateSelect}
                        value={selectedMailState}
                        onChange={(selection) => {
                          setSelectedMailState(selection);
                          setEntityField("mail_state", selection?.value || null);
                        }}
                        onBlur={handleEntityBlur}
                        options={stateOptions}
                        isClearable
                      />
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-0 align-items-center" controlId="mail_code">
                    <Form.Label column sm={4} className="text-sm-end mb-0">Postcode</Form.Label>
                    <Col sm={8}>
                      <Form.Control
                        id={elementIds.mailCodeInput}
                        value={entity.mail_code || ""}
                        onChange={(event) => setEntityField("mail_code", sanitizePostcode(event.target.value))}
                        onBlur={handleEntityBlur}
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                      />
                    </Col>
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <Card className="slm-detail-card slm-notes-card h-100">
                <Card.Header>Notes</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-0" controlId="notes">
                    <Form.Label className="visually-hidden">Notes</Form.Label>
                    <Form.Control
                      id={elementIds.notesInput}
                      value={entity.notes || ""}
                      onChange={(event) => setEntityField("notes", event.target.value)}
                      onBlur={handleEntityBlur}
                      as="textarea"
                      className="slm-notes-input"
                      rows={10}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mt-3" id={elementIds.contactsSection}>
            <Col xs={12}>
              <Row className="mb-2">
                {/* <Col>
                  <h6 className="mb-0">Entity Contacts</h6>
                </Col> */}
                <Col className="text-end">
                  <Button id={elementIds.contactsAddButton} variant="primary" size="sm" onClick={addContactRecord}>
                    Add New
                  </Button>
                </Col>
              </Row>
              <div className="slm-entity-contacts-table-wrap">
                <GenericTable
                  columns={contactsColumns}
                  data={entityContacts}
                  section="slm-entity-detail-contacts"
                  elementIds={elementIds.contactsTable}
                  pageSize={PAGE_SIZES.slmEntityDetail}
                  enablePagination={true}
                  hasHeaderSorting={true}
                  tableStyle={{ minWidth: "1100px" }}
                  enforceMinRowsHeight={true}
                  minVisibleRows={5}
                  meta={{
                    updateData: updateContactState,
                    handleSave: saveContact,
                  }}
                />
              </div>
            </Col>
          </Row>

          <input type="hidden" value={currentEntityId || ""} readOnly />
        </Form>
      )}
    </div>
  );
}

export default SLMEntityDetail;
